import { useEffect, useMemo } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { AdministrationApi, OperationsApi } from '@Apis';
import { t } from 'i18next';
import { dayjs } from '@Utils/time';
import { MitigationType } from '../../types/remediation';

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortingState {
  columnId: string;
  direction: 'asc' | 'desc';
}

export const useAnalysisData = (
  companyGuid: string | undefined,
  feedbackId: string | undefined,
  pagination: PaginationState,
  sorting: SortingState
) => {
  const queryClient = useQueryClient();

  // Fetch feedback result
  const { data: resultData, isLoading: isResultLoading } = useQuery({
    queryKey: ['feedbackResult', companyGuid, feedbackId],
    queryFn: () =>
      OperationsApi.apiGetFeedbackResult(companyGuid || '', feedbackId || ''),
    enabled: !!feedbackId && !!companyGuid,
    placeholderData: keepPreviousData,
    select: (response) => response.data,
  });

  // Fetch feedback mail
  const { data: mailData, isLoading: isMailLoading } = useQuery({
    queryKey: ['feedbackMail', companyGuid, feedbackId],
    queryFn: () =>
      OperationsApi.apiGetFeedbackMail(companyGuid || '', feedbackId || ''),
    enabled: !!feedbackId && !!companyGuid,
    placeholderData: keepPreviousData,
    select: (response) => response.data,
    staleTime: Infinity, // Never refetch
  });

  // Fetch related entries
  const {
    data: mitigationData,
    isFetching: isMitigationLoading,
    error: mitigationError,
  } = useQuery({
    queryKey: [
      'feedbackRelatedEntries',
      companyGuid,
      feedbackId,
      pagination,
      sorting,
    ],
    queryFn: () =>
      OperationsApi.apiGetFeedbackRelatedEntries(
        companyGuid || '',
        feedbackId || '',
        {
          offset: pagination.pageIndex * pagination.pageSize,
          limit: pagination.pageSize,
          sort: sorting.columnId,
          order: sorting.direction,
        }
      ),
    enabled: !!feedbackId && !!companyGuid,
    placeholderData: keepPreviousData,
    select: (response) => response.data,
  });

  // Fetch EUQ settings using company_guid from mitigationData
  const { data: euqSettings } = useQuery({
    queryKey: ['euqSettings', mitigationData?.contentList?.[0]?.company_guid],
    queryFn: () =>
      AdministrationApi.getEuqSettings(
        mitigationData?.contentList?.[0]?.company_guid
      ),
    enabled: !!mitigationData?.contentList?.[0]?.company_guid,
    select: (response) => response.data,
  });

  // Periodic refetch when code = 2 (still generating)
  useEffect(() => {
    if (mitigationData?.code === 2) {
      const interval = setInterval(() => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackRelatedEntries', companyGuid, feedbackId],
        });
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [mitigationData?.code, queryClient, companyGuid, feedbackId]);

  // Mutation for executing feedback actions
  const executeFeedbackActionsMutation = useMutation({
    mutationFn: (actionIds: string[]) =>
      OperationsApi.apiExecuteFeedbackActions(
        companyGuid || '',
        feedbackId || '',
        actionIds
      ),
    onSuccess: () => {
      // Refresh the related entries data after successful execution
      queryClient.invalidateQueries({
        queryKey: ['feedbackRelatedEntries', companyGuid, feedbackId],
      });
    },
  });

  // Calculate mitigation progress
  const { mitigationProgress, mitigationDone, mitigationTotal } =
    useMemo(() => {
      const statistic = mitigationData?.statistic;
      if (!statistic || statistic.totalAffectedCount === 0) {
        return { mitigationProgress: 0, mitigationDone: 0, mitigationTotal: 0 };
      }
      return {
        mitigationProgress: Math.floor(
          (statistic.doneCount / statistic.totalAffectedCount) * 100
        ),
        mitigationDone: statistic.doneCount,
        mitigationTotal: statistic.totalAffectedCount,
      };
    }, [mitigationData?.statistic]);

  // Process result data
  const result = useMemo(() => {
    const category = resultData?.rescanResult?.type || '';
    const severity = resultData?.rescanResult?.confidenceLevel || 'low';
    const categoryLabels: Record<string, string> = {
      spam: t('operations.euq.reportAs.type.spam'),
      threat: t('operations.euq.reportAs.type.phishing'),
      normal: t('operations.euq.reportAs.type.noRisk'),
    };
    const analysisResult =
      severity === 'low'
        ? t('operations.euq.result.flagged')
        : categoryLabels[category] || '';

    // Determine mitigation type based on global setting
    const isAutoRemediationEnabled =
      euqSettings?.euq_feedback_phishing_spam_auto_remediation?.isEnabled;

    // Determine confidence level from severity
    const isHighConfidence = severity !== 'low';

    // Default to manual mitigation if mitigationData query is done but has no content
    const hasMitigationData =
      mitigationData?.contentList && mitigationData.contentList.length > 0;
    const isMitigationQueryDone =
      !isMitigationLoading && mitigationData !== undefined;

    let mitigationType = MitigationType.MANUAL; // Default to manual

    // Always use manual mitigation for flagged (low confidence) results
    if (severity === 'low') {
      mitigationType = MitigationType.MANUAL;
    } else if (
      isMitigationQueryDone &&
      hasMitigationData &&
      isAutoRemediationEnabled
    ) {
      mitigationType = MitigationType.AUTOMATIC;
    }

    return {
      msgSubject: mailData?.metadata?.messageSubject || '',
      messageId: mailData?.metadata?.messageId || '',
      timestamp: mailData?.metadata?.messageArriveTime || '',
      sender: mailData?.metadata?.messageSender || '',
      recipient: mailData?.metadata?.messageRecipients || '',
      reporter: mailData?.metadata?.requestedBy || '',
      attachments: mailData?.metadata?.messageAttachments || [],
      messageHeaders: mailData?.metadata?.messageHeaders || [],
      mailPreviewContent: mailData?.text || '',
      mailHtmlContent: mailData?.html || '',
      analysisResult,
      mitigationType,
      mitigationProgress,
      summary: resultData?.rescanResult?.reasoning?.summary || '',
      // For TP (spam/threat): use suspiciousItems; for FP (normal): use confidenceItems
      items:
        resultData?.rescanResult?.reasoning?.suspiciousItems ||
        resultData?.rescanResult?.reasoning?.confidenceItems ||
        [],
      isHighConfidence,
      customRule: resultData?.customRule || {},
      caseState: resultData?.caseState || '',
      processingState: resultData?.processingState || '',
    };
  }, [
    resultData,
    mailData,
    mitigationProgress,
    euqSettings,
    isMitigationLoading,
    mitigationData,
  ]);

  // Format timestamp
  const formattedTimestamp = useMemo(() => {
    return result.timestamp
      ? dayjs(result.timestamp).utc(true).format('YYYY/MM/DD HH:mm')
      : null;
  }, [result.timestamp]);

  const mitigationNotRequired = useMemo(() => {
    return (
      !isMitigationLoading &&
      (mitigationData?.total ?? 0) === 0 &&
      mitigationData?.code !== 2
    );
  }, [isMitigationLoading, mitigationData]);

  const mitigationsAvailable = useMemo(() => {
    return (mitigationData?.total ?? 0) > 0;
  }, [mitigationData]);

  return {
    resultData,
    mailData,
    mitigationData,
    euqSettings,
    result,
    formattedTimestamp,
    mitigationDone,
    mitigationTotal,
    isResultLoading,
    isMailLoading,
    isMitigationLoading,
    mitigationError,
    mitigationNotRequired,
    mitigationsAvailable,
    executeFeedbackActionsMutation,
  };
};
